import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";

import DataTable, {
  createColumnHelper,
  createFilter,
  sorting,
} from "veneer/DataTable";
import styled from "veneer/styled";

import { uptimeReportSelector } from "cortex/ui/src/fleet/redux-store/ui-state/report-uptime/selectors";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { Network } from "cortex/ui/src/network/model";
import {
  networksSelector,
  networksStoresLoadedSelector,
} from "cortex/ui/src/redux-store/networks";
import { NavLoader } from "cortex/ui/src/ui/loader";
import Notification from "cortex/ui/src/ui/notification";
import {
  formatDateTimeLocal,
  formatPercentage,
} from "cortex/ui/src/ui/table/formats";
import { getErrorMessage } from "cortex/ui/src/util/response";
import { Container, Panel } from "cortex/ui/src/view/layout";

import ReportFilter from "./ReportFilter";
import UptimeChart from "./UptimeChart";
import {
  type FetchUptimeReportArgs,
  fetchUptimeReport,
  unloadUptimeReport,
} from "../action";
import { UptimeReport } from "../model";

export default function UptimeReportPage() {
  const { networkId, start, end } = useParams<{
    networkId?: string;
    start?: string;
    end?: string;
  }>();
  const dispatch = useAppDispatch();
  const networks = useAppSelector(networksSelector);
  const storesLoaded = useAppSelector(networksStoresLoadedSelector);
  const report = useAppSelector(uptimeReportSelector);
  const network: Network | undefined = useMemo(
    () => (networkId ? networks?.[networkId] : undefined),
    [networks, networkId],
  );
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (!network || !start || !end) {
      unloadUptimeReport(dispatch);
    }
  }, [dispatch, end, network, start]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      unloadUptimeReport(dispatch);
    };
  }, [dispatch]);

  useEffect(() => {
    const canFetch = Boolean(network && start && end);
    if (!canFetch) return;

    const args: FetchUptimeReportArgs = {
      networkId: network!.id,
      start: start!,
      end: end!,
    };

    setSubmitted(true);

    fetchUptimeReport(dispatch, args)
      .catch((error) => {
        const msg = getErrorMessage(error, "Failed to fetch uptime report.");
        setErrorMessage(msg);
      })
      .finally(() => setSubmitted(false));
  }, [dispatch, end, network, start]);

  const exportFileName = useCallback(
    (prefix: string) => {
      const name = network ? network.name : "";
      return `${prefix}_${name}_${start ?? ""}_${end ?? ""}.csv`;
    },
    [end, network, start],
  );

  const downloadCsv = useCallback(
    (rows: ReadonlyArray<UptimeReport>) => {
      const header = [["Date", "Uptime"]];
      const lines = rows.map((row) => {
        const dateStr = moment.isMoment(row.date)
          ? row.date.format("YYYY-MM-DD")
          : moment(row.date).format("YYYY-MM-DD");
        const uptime = Number.isFinite(row.uptime) ? row.uptime : 0;
        return [dateStr, uptime];
      });

      const escaped = [header, lines]
        .flat()
        .map((arr) =>
          arr.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");

      const blob = new Blob([escaped], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFileName("uptime");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    [exportFileName],
  );

  const renderNetworkUptimeReport = useCallback(() => {
    if (!report || Object.keys(report).length === 0) {
      return <p>No data to display.</p>;
    }

    const data: ReadonlyArray<UptimeReport> = Object.keys(report).map((d) => ({
      date: moment(d),
      uptime: report[d],
    }));

    const filter = createFilter<UptimeReport>("date");
    const columnHelper = createColumnHelper<UptimeReport>();

    const columns = [
      columnHelper.accessor("date", {
        header: "Date",
        cell: (prop) => {
          const date = prop.getValue();
          if (!network) {
            return formatDateTimeLocal(date, "MM/DD/YYYY");
          }

          const url =
            `/diagnostics/uptime-report/${network.id}/` +
            `${start}/${end}/devices/` +
            `${date.format("YYYY-MM-DD")}`;

          return (
            <Link to={url}>{formatDateTimeLocal(date, "MM/DD/YYYY")}</Link>
          );
        },
        sortingFn: sorting.datetime((row) => row.date),
      }),
      columnHelper.accessor("uptime", {
        header: "Uptime",
        cell: (prop) => formatPercentage(prop.getValue()),
        sortingFn: sorting.number((row) => row.uptime),
      }),
    ];

    return (
      <DataTable
        columns={columns}
        filter={filter}
        initialPageSize={10}
        initialSort={{ id: "date", desc: true }}
        onExport={(rows) => downloadCsv(rows)}
        rows={data}
      />
    );
  }, [downloadCsv, network, report]);

  const title = network ? `Network Uptime: ${network.name}` : "Network Uptime";

  if (!storesLoaded) {
    return (
      <Container title="Uptime Report">
        <NavLoader.Proxy />
      </Container>
    );
  }

  return (
    <Container hideBox title="Uptime Report">
      {errorMessage && <Notification error={errorMessage} />}
      <ReportFilter
        end={end}
        network={network}
        start={start}
        submitted={submitted}
      />
      <Panel borderRadius={2} title={title}>
        {network && Object.keys(report || {}).length > 0 ? (
          <UptimeChart data={report} />
        ) : null}
        {renderNetworkUptimeReport()}
      </Panel>
      <Panel borderRadius={2} noHeader>
        <span>
          This report shows daily uptime percentage of a selected network. The
          uptime percentage of a network is the average uptime of its
          constituent devices:
        </span>
        <BlockQuote>
          Daily uptime of a network = (total uptime of devices in the network) /
          (# of devices in the network) x 100
        </BlockQuote>
        <span>
          To view device-specific uptime percentages:
          <ol>
            <li>Run the network uptime report.</li>
            <li>Click the date value you want to explore.</li>
            <li>
              Click the device id to view historical uptimes for that device.
            </li>
          </ol>
          The uptime percentage for a device is calculated by dividing the
          number of active windows by the total window count:
        </span>
        <BlockQuote>
          Daily uptime of a device = (# of windows) / (total window count in a
          day) x 100
        </BlockQuote>
        <span>
          A device is considered <b>online</b> if it has made a connection to
          our system at least once within a 5 minute interval.
        </span>
        <span>
          If a device is moved to a new network, its uptime on that date will be
          calculated from the change time to the end of the day. The calculated
          uptime will only affect its new network. For example if a device is
          moved from sandbox to production in the middle of the day, only the
          rest of the day will be considered when calculating the uptime. The
          uptime report would show:
        </span>
        <span>
          Sandbox network:
          <ul>
            <li>Jan 1st, device-1: 100%</li>
            <li>Jan 2nd, device-1: 50%</li>
          </ul>
        </span>
        <span>
          Production network:
          <ul>
            <li>Jan 2nd, device-1: 50%</li>
            <li>Jan 3rd, device-1: 100%</li>
          </ul>
        </span>
      </Panel>
    </Container>
  );
}

const BlockQuote = styled("pre")({
  fontFamily: "monospace",
  fontSize: "small",
  borderLeft: "3px solid #ccc",
  marginTop: 15,
  padding: 10,
});
